/**
 * Renders a single physical room as its own form section. Occupancy is
 * rate-derived and shown read-only; the user only edits per-room
 * preferences (bed type, extras, note).
 */

import type { EligibleRoomExtra, Occupancy } from '@elara/domain/models/schemas/booking-schemas';
import type { BedType } from '@elara/domain/models/schemas/room-schemas';

import type { RoomFormState } from './types.js';

import React, { useEffect } from 'react';

import { formatRoomSize, getBedTypeIcon } from '@elara/client-core/lib/room-utils';
import { cn, formatBookingOccupancy, formatPrice } from '@elara/client-core/lib/utils';

import { AppImage, Stepper } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { RadioGroup, RadioGroupItem } from '#ui/radio-group.js';

import { Field } from './FormControls.js';

export function RoomFormSection({
  title,
  counterLabel,
  occupancy,
  bedTypes,
  state,
  extras,
  thumbnailUrl,
  roomSize,
  showBedPreferenceNote,
  onChange,
}: {
  title: string;
  /** "Room N of M" across the whole booking; hidden when undefined. */
  counterLabel?: string;
  /** Read-only occupancy carried by this room (sourced from the rate). */
  occupancy: Occupancy;
  /**
   * The room's catalogue of bed configurations. The chosen
   * `sabreBedTypeCode` is sent to Sabre's
   * `CreatePassengerNameRecord.Room.BedTypeCode` at booking time. EAN-source
   * rates honour the preference; other rate sources treat it as a hint.
   */
  bedTypes: BedType[];
  state: RoomFormState;
  /** Server-resolved eligible extras for this room (already filtered + capped). */
  extras: EligibleRoomExtra[];
  /** First image URL from the room option; falls back to a bed icon when absent. */
  thumbnailUrl?: string;
  /** Free-text room size (e.g. "30 m²" / "350 sq ft"); hidden when absent. */
  roomSize?: string;
  /** When true, renders the "bed preferences are subject to availability" note under the bed picker. */
  showBedPreferenceNote?: boolean;
  onChange: (patch: Partial<RoomFormState>) => void;
}): React.ReactNode {
  // Unique prefix per RoomFormSection instance — avoids duplicate `id`/
  // `htmlFor` collisions when multiple rooms render the same `bed-pref-${code}` IDs.
  const reactId = React.useId();

  // Persist the default bed preference (first choosable bed) into form state
  // when the user hasn't picked one yet — so the saved booking carries the
  // implied selection rather than `undefined`.
  useEffect(() => {
    if (state.bedTypePreference != null) return;
    const firstCode = bedTypes.find((b) => b.sabreBedTypeCode != null && b.description)?.sabreBedTypeCode;
    if (firstCode != null) onChange({ bedTypePreference: firstCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.bedTypePreference, bedTypes]);

  function setExtraQty(extraId: string, quantity: number) {
    onChange({ extras: { ...state.extras, [extraId]: Math.max(0, quantity) } });
  }

  return (
    <section>
      <div className="flex items-center gap-3">
        {thumbnailUrl ? (
          <AppImage src={thumbnailUrl} alt={title} className="h-10 w-10 shrink-0 rounded-sm object-cover" />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-background-tertiary text-muted-foreground/40">
            <MaterialIcon icon="hotel" size={IconSizes.sm} />
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="truncate text-sm font-medium">{title}</h3>
          <div className="text-xs text-muted-foreground flex items-center gap-2.5">
            <div className="flex shrink-0 items-center gap-1">
              <MaterialIcon icon="group" size={IconSizes.xs} />
              <span>{formatBookingOccupancy(occupancy)}</span>
            </div>
            {roomSize ? (
              <div className="flex shrink-0 items-center gap-1">
                <MaterialIcon icon="square_foot" size={IconSizes.xs} />
                <span>{formatRoomSize(roomSize)}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {(() => {
          const choosable = bedTypes.filter((b) => b.sabreBedTypeCode != null && b.description);
          if (choosable.length === 0) return null;
          const fallbackCode = choosable[0]!.sabreBedTypeCode!;
          const value = String(state.bedTypePreference ?? fallbackCode);
          return (
            <Field label="Bed preference">
              <RadioGroup
                value={value}
                onValueChange={(v) => onChange({ bedTypePreference: v === '' ? undefined : Number(v) })}
                className="grid grid-cols-1 gap-4 md:grid-cols-2"
              >
                {choosable.map((bed) => {
                  const id = `${reactId}-bed-pref-${bed.sabreBedTypeCode}`;
                  const selected = value === String(bed.sabreBedTypeCode);
                  return (
                    <label
                      key={bed.sabreBedTypeCode}
                      htmlFor={id}
                      className={cn(
                        'flex cursor-pointer items-center justify-between gap-3 rounded border p-3 transition-colors',
                        selected ? 'border-primary' : 'border-border hover:border-input',
                      )}
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium">
                        <MaterialIcon icon={getBedTypeIcon(bed.sabreBedTypeCode)} size={IconSizes.sm} />
                        <span>{bed.count > 1 ? `${bed.count} × ${bed.description}` : bed.description}</span>
                      </span>
                      <RadioGroupItem id={id} value={String(bed.sabreBedTypeCode)} />
                    </label>
                  );
                })}
              </RadioGroup>
              {showBedPreferenceNote ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Bed preferences are subject to availability at the property.
                </p>
              ) : null}
            </Field>
          );
        })()}

        {extras.length > 0 ? (
          <Field label="Extras">
            <ul className="gap-4 grid grid-cols-1 md:grid-cols-2">
              {extras.map((extra) => {
                const qty = state.extras[extra.id] ?? 0;
                const max = extra.maxQuantity;
                return (
                  <li key={extra.id} className="flex items-start justify-between gap-3 rounded border border-input p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{extra.name ?? extra.id}</p>
                      {extra.description ? <p className="text-xs text-muted-foreground">{extra.description}</p> : null}
                      {extra.amount != null ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {extra.amount === 0 ? 'Free' : `${formatPrice(extra.amount, extra.currency ?? 'USD')} each`}
                          {extra.amount !== 0 && extra.scope ? ` · ${extra.scope.replaceAll('_', ' ')}` : ''}
                        </p>
                      ) : null}
                    </div>
                    <Stepper value={qty} onChange={(v) => setExtraQty(extra.id, v)} min={0} max={max} borderless />
                  </li>
                );
              })}
            </ul>
          </Field>
        ) : null}
      </div>
    </section>
  );
}
